// ---------------------------------------------------------------------------
// Presentación de alertas: una sola forma de armar el "dónde" y el "qué" de
// una alerta de /api/list-alerts-custom, compartida por la campana de
// notificaciones (Layout.vue) y la tarjeta "Alertas recientes" del Dashboard.
//
// Antes cada vista armaba lo suyo: la campana componía el mensaje completo y
// el Dashboard solo intentaba `a.mensaje` (campo que la API no manda), así
// que la tarjeta terminaba mostrando el texto genérico "Alerta" sin decir de
// qué estación, variable ni valor se trataba.
// ---------------------------------------------------------------------------
import { formatNumber, EMPTY_VALUE } from './formatProjectData';

// Severidades: misma paleta y etiquetas que los CORREOS de alerta
// (agrobyte-process/src/alertas/correo.js), para que la app pinte el mismo
// color que llegó por email. `recuperada` existe por si el endpoint la
// entrega en el futuro; hoy el listado trae info/advertencia/critica.
export const SEVERIDAD_META = {
  critica:     { etiqueta: 'Crítica',     color: '#c0392b', fondo: '#fdecea' },
  advertencia: { etiqueta: 'Advertencia', color: '#d9822b', fondo: '#fef3e6' },
  info:        { etiqueta: 'Información', color: '#2f6fb6', fondo: '#e9f2fc' },
  recuperada:  { etiqueta: 'Recuperada',  color: '#0a7d5a', fondo: '#e8f6f0' }
};

// Severidad efectiva de una alerta del listado. La API ya la resuelve
// (`severidad`, espejo del motor de correos); el respaldo local cubre
// respuestas cacheadas anteriores a ese campo, con la misma regla por
// defecto: agua disponible -> crítica, resto -> advertencia.
export function alertSeverity(alerta) {
  const s = alerta?.severidad;
  if (s && SEVERIDAD_META[s]) return s;
  if (alerta?.esAD || alerta?.tipoAlerta === 'ad_cuartel') return 'critica';
  return 'advertencia';
}

export function alertSeverityMeta(alerta) {
  return SEVERIDAD_META[alertSeverity(alerta)];
}

// "Dónde": nombre del sensor/estación/cuartel de la alerta, con respaldos.
export function formatAlertStation(alerta) {
  return (
    alerta?.nombreSensor ||
    alerta?.zona ||
    alerta?.estacion ||
    alerta?.cuartel ||
    (alerta?.idSensor != null ? `Sensor ${alerta.idSensor}` : '') ||
    'Alerta'
  );
}

// "Qué": variable · condición umbral unidad — registrado: valor unidad.
// `valorAlerta` llega como Number desde la API (ver doc/FORMATO_REGIONAL.md):
// el locale se aplica acá, al mostrarlo.
export function formatAlertMessage(alerta) {
  // Alerta de agua disponible del cuartel: mensaje claro y dedicado.
  if (alerta?.esAD || alerta?.tipoAlerta === 'ad_cuartel') {
    return 'Agua disponible del cuartel cerca del umbral de riego. Conviene regar.';
  }
  const variable = alerta?.variable || '';
  const cond = alerta?.textCondicion || '';
  const unidad = alerta?.unidadMedida || '';
  const umbral = (alerta?.valorCondicion ?? '') !== '' ? alerta.valorCondicion : '';
  const partes = [];
  if (variable) partes.push(variable);
  if (cond) partes.push(`${cond} ${umbral} ${unidad}`.replace(/\s+/g, ' ').trim());
  let msg = partes.join(' · ');
  const valor = alerta?.valorAlerta;
  if (valor != null && valor !== '') {
    const valorFmt = formatNumber(valor);
    if (valorFmt !== EMPTY_VALUE) {
      msg += `${msg ? ' — ' : ''}registrado: ${valorFmt} ${unidad}`.replace(/\s+$/, '');
    }
  }
  return msg || 'Alerta';
}

export default { formatAlertStation, formatAlertMessage, alertSeverity, alertSeverityMeta, SEVERIDAD_META };
